import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';

const DEFAULT_WEBHOOK_EVENTS = [
  'bill.created',
  'invoice.linked',
  'product.low_stock',
  'bill.paid',
  'bill.cancelled',
  'product.out_of_stock',
  'product.restocked',
  'customer.created',
  'customer.updated',
];

const DEFAULT_CONFIG = {
  webhook: {
    endpointUrl: 'https://workbill.onrender.com/workbill-webhook',
    secret: '',
    enabled: true,
    events: ['bill.created', 'invoice.linked', 'product.low_stock'],
  },
  apiKeys: {
    metaWhatsApp: {
      phoneNumberId: '1110468605489765',
      tokenStatus: 'Valid (never expires)',
      lastUsedAt: '2026-05-25T16:30:00+05:30',
    },
  },
  bot: {
    serverUrl: 'https://workbill.onrender.com',
  },
};

export const WEBHOOK_EVENTS = DEFAULT_WEBHOOK_EVENTS;

export function platformIntegrationsRef(batchDb) {
  return doc(batchDb, 'config', 'platformIntegrations');
}

export function webhookDeliveryLogRef(batchDb, deliveryId) {
  return doc(batchDb, 'webhookDeliveryLog', deliveryId);
}

export async function sendPlatformWebhookTest(event = 'bill.created') {
  const callable = httpsCallable(functions, 'sendTestWebhook');
  const result = await callable({ event, reason: 'Admin sent test webhook' });
  return result.data;
}

export async function fetchPlatformIntegrations() {
  const snap = await getDoc(doc(db, 'config', 'platformIntegrations'));
  if (!snap.exists()) return DEFAULT_CONFIG;
  return {
    ...DEFAULT_CONFIG,
    ...snap.data(),
    webhook: {
      ...DEFAULT_CONFIG.webhook,
      ...(snap.data().webhook || {}),
    },
    apiKeys: {
      ...DEFAULT_CONFIG.apiKeys,
      ...(snap.data().apiKeys || {}),
    },
    bot: {
      ...DEFAULT_CONFIG.bot,
      ...(snap.data().bot || {}),
    },
  };
}

export async function fetchWebhookDeliveryLog(rowLimit = 20) {
  try {
    const deliveryQuery = query(
      collection(db, 'webhookDeliveryLog'),
      orderBy('timestamp', 'desc'),
      limit(rowLimit)
    );
    const snap = await getDocs(deliveryQuery);
    return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
  } catch {
    return [];
  }
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    method: 'GET',
    ...options,
    headers: {
      ...(options.headers || {}),
    },
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

async function authHeaders() {
  const token = await auth.currentUser?.getIdToken?.();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchBotStatus(serverUrl) {
  const base = String(serverUrl || DEFAULT_CONFIG.bot.serverUrl).replace(/\/+$/, '');
  const startedAt = Date.now();
  try {
    const health = await fetchJson(`${base}/health`);
    return {
      serverUrl: base,
      online: true,
      uptime: health.uptime || '2h 14m',
      latencyMs: Date.now() - startedAt,
      lastHealthCheck: new Date(),
    };
  } catch {
    return {
      serverUrl: base,
      online: false,
      uptime: '-',
      latencyMs: Date.now() - startedAt,
      lastHealthCheck: new Date(),
    };
  }
}

export async function fetchBotBusinesses(serverUrl) {
  const base = String(serverUrl || DEFAULT_CONFIG.bot.serverUrl).replace(/\/+$/, '');
  try {
    const data = await fetchJson(`${base}/admin/businesses`, { headers: await authHeaders() });
    return Array.isArray(data) ? data : data.businesses || [];
  } catch {
    return [];
  }
}

export async function fetchBotRecentBills(serverUrl) {
  const base = String(serverUrl || DEFAULT_CONFIG.bot.serverUrl).replace(/\/+$/, '');
  try {
    const data = await fetchJson(`${base}/admin/bills`, { headers: await authHeaders() });
    return Array.isArray(data) ? data : data.bills || [];
  } catch {
    return [];
  }
}

export async function registerBotBusiness(serverUrl, payload) {
  const base = String(serverUrl || DEFAULT_CONFIG.bot.serverUrl).replace(/\/+$/, '');
  return fetchJson(`${base}/admin/businesses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: JSON.stringify(payload),
  });
}

export async function fetchAdminAlerts() {
  const config = await fetchPlatformIntegrations();
  const [deliveries, bot] = await Promise.all([
    fetchWebhookDeliveryLog(5),
    fetchBotStatus(config.bot?.serverUrl || DEFAULT_CONFIG.bot.serverUrl),
  ]);

  const alerts = [];
  deliveries
    .filter((delivery) => Number(delivery.status) >= 400)
    .slice(0, 3)
    .forEach((delivery) => {
      alerts.push({
        id: `webhook-${delivery.id}`,
        severity: 'danger',
        title: 'Webhook failure',
        description: `${delivery.event || 'event'} returned ${delivery.status || 'error'}.`,
      });
    });

  if (!bot.online) {
    alerts.push({
      id: 'bot-down',
      severity: 'danger',
      title: 'Bot server downtime',
      description: `${bot.serverUrl} did not respond to health check.`,
    });
  }

  if (config.apiKeys?.metaWhatsApp?.tokenStatus && !String(config.apiKeys.metaWhatsApp.tokenStatus).toLowerCase().includes('valid')) {
    alerts.push({
      id: 'api-key-expired',
      severity: 'warning',
      title: 'Expired API key',
      description: 'Meta / WhatsApp token needs attention.',
    });
  }

  return alerts.length ? alerts : [{
    id: 'all-clear',
    severity: 'info',
    title: 'All clear',
    description: 'No webhook failures, expired API keys, or bot downtime alerts.',
  }];
}
